/**
 * Common ProductCard component used by other modules as a reusable component which returns the ui for a product card.
 *
 * @module views/Molecules/ProductCard
 * @memberof -Common
 */
import './ProductCard.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { ProductCardDefault as PalProductCardDefault } from 'web-palette/dist/components/ProductCardDefault/ProductCardDefault';

import Button from '@ulta/core/components/Button/Button';
import Image from '@ulta/core/components/Image/Image';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import useLoader from '@ulta/core/hooks/useLoader/useLoader';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { hasItems } from '@ulta/core/utils/array/array';
import { constants } from '@ulta/core/utils/constants/constants';
import { handleRedirect } from '@ulta/core/utils/handleLocation/handleLocation';

import { useProductRailHorizontalContext } from '@ulta/providers/ProductRailHorizontalProvider/ProductRailHorizontalProvider';

import RemoveItem from '@ulta/components/RemoveItem/RemoveItem';

import interpolateString from '@ulta/utils/interpolateString/interpolateString';

import * as utils from './ProductCard';

/**
 * Represents a ProductCard component
 *
 * @method
 * @param {ProductCardProps} props - React properties passed from composition
 * @returns ProductCard
 */
export const ProductCard = function( props ){
  const {
    carouselIndex,
    componentKey,
    invokeMutation,
    productId,
    skuId,

    /* actions */
    action,
    addToBagAction,
    viewOptionAction,

    /* text */
    additionalOffersText,
    brandName,
    messages,
    productName,
    promoText,
    sponsoredBadgeLabel,
    variantLabel,

    /* badge */
    badge,
    badgeTags, // tagsTop in palette (NEED DXL to add this for rails)
    productCardTags, // tagsBottom in palette (NEED DXL to add this for rails)

    /* rating */
    rating,
    reviewAccessibilityLabel,
    reviewCount,

    /* price */
    kitPrice,
    kitPriceAccessibilityLabel,
    listPrice,
    listPriceAccessibilityLabel,
    originalPriceLabel,
    priceLabel,
    salePrice,
    salePriceAccessibilityLabel,

    /* image */
    image,
    prefetchImage = defaultProps.prefetchImage,

    /* favorites */
    ariaLabelBookmark,
    displayBookmark = defaultProps.displayBookmark,
    removeFavoriteAction,
    removeFavoriteDialog,

    /* useIntersectionObserver */
    onDataCaptureEvent,
    onIntersectionEvent,
    root = defaultProps.root,
    rootMargin = defaultProps.rootMargin,

    /* old settings */
    size = defaultProps.size,
    type = defaultProps.type
  } = props;

  /** variables */
  const productRef = useRef();
  const hasFiredDCEvent = useRef( false );
  const [isButtonClicked, setIsButtonClicked] = useState( false );
  const [loader, showLoader, hideLoader] = useLoader( { loading: false, isPageLoader: true, color: 'orange-400' } );
  const { isOverlay } = useOverlay();

  const bookmarkLabel = ariaLabelBookmark || removeFavoriteAction?.label;
  const imgDimension = utils.setImageParams( type, size ) * ( global.devicePixelRatio || 1 );
  const derivedBadge = badgeTags ? null : badge; // badgeTags check

  /** Accessibility labels */
  const accessibilityLabelKitPrice = interpolateString( kitPriceAccessibilityLabel, [kitPrice] );
  const accessibilityLabelListPrice = interpolateString( listPriceAccessibilityLabel, [listPrice] );
  const accessibilityLabelOriginalPrice = interpolateString( originalPriceLabel, [listPrice] );
  const accessibilityLabelSalePrice = interpolateString( salePriceAccessibilityLabel, [salePrice] );
  const accessibilityRating = interpolateString( reviewAccessibilityLabel, [rating, reviewCount] );

  const screenReaderText = `${brandName} ${productName}`;

  /*
   * When rendered inside a carousel, the Swiper container provides the
   * accessibility roles. Outside of a carousel, we set role="group"
   * so screen readers recognize the card as a single group
   * of related product information.
   */
  const productCardRole = carouselIndex !== undefined ? undefined : 'group';

  /** Objects for external media and actions coming from Amplience */
  let formattedMedia;
  let formattedAction;
  let formattedActionFullyClickableable;
  let formattedFavorite;

  // We are setting an empty object as default value for visibleSlidesIndexes because
  // the context (visibleSlidesIndexes) is only available for product cards inside a swiper carousel
  const { visibleSlidesIndexes } = useProductRailHorizontalContext() || {};

  // For ada tabbing through carousel, we need to set the tabindex to -1 if the product is not visible in the carousel
  const isVisibleInCarousel = visibleSlidesIndexes?.length && !!visibleSlidesIndexes?.includes( carouselIndex );
  const isFocusable = isVisibleInCarousel !== false;

  // Handles last scrolled threshold
  const { intersectionRatio, hasIntersected } = useIntersectionObserver( productRef, {
    root: root,
    rootMargin: rootMargin,
    threshold: [0, 0.5, 1.0]
  } );

  const atbAction = {
    ...addToBagAction,
    variables :{
      moduleParams: {
        skuId,
        productId
      }
    }
  };

  const isAddOverlay = isOverlay( atbAction.navigationType );
  if( !isAddOverlay ){
    atbAction.variables.moduleParams.quantity = 1;
  }

  useEffect( () => {
    utils.handleIntersection(
      { hasFiredDCEvent, hasIntersected, intersectionRatio, skuId },
      { onDataCaptureEvent, onIntersectionEvent }
    );
  }, [
    hasFiredDCEvent, hasIntersected, intersectionRatio, onDataCaptureEvent,
    onIntersectionEvent, skuId
  ] );

  // Compose action.after function for ATB action
  const handleActionAfter = useCallback(
    utils.composeActionAfter( { hideLoader } ), [hideLoader]
  );

  // Compose onClick for Product Card ATB
  // -> handles invoking the action from DXL
  const handleOnClick = useCallback(
    utils.composeOnClick(
      { productId, skuId, isAddOverlay, atbAction },
      { invokeMutation, showLoader, handleActionAfter }
    ),
    [invokeMutation, productId, skuId, isAddOverlay, atbAction, showLoader, handleActionAfter]
  );

  /** Formatters for incoming media and actions */
  if( addToBagAction || viewOptionAction ){
    formattedAction = utils.createButton(
      {
        addToBagAction,
        atbAction,
        brandName,
        isFocusable,
        productName,
        viewOptionAction
      }, { handleOnClick }
    );
  }

  if( action ){
    formattedActionFullyClickableable = utils.createAbsoluteLink(
      { action, isFocusable, screenReaderText }
    );
  }

  if( bookmarkLabel ){
    formattedFavorite = utils.createFavoritesButton(
      {
        bookmarkLabel,
        image,
        isButtonClicked,
        listPrice,
        messages,
        productName,
        removeFavoriteAction,
        removeFavoriteDialog,
        salePrice,
        skuId
      },
      { invokeMutation, setIsButtonClicked }
    );
  }

  if( image ){
    formattedMedia = utils.createImage(
      { image, imgDimension, prefetchImage, screenReaderText }
    );
  }

  return (
    <div
      className='ProductCard'
      ref={ productRef }
      data-visible={ intersectionRatio === 1 }
      data-item-id={ skuId }
      role={ productCardRole }
      aria-label='product'
    >
      { loader }
      <PalProductCardDefault
        additionalOffersText={ additionalOffersText }
        badge={ derivedBadge }
        brandName={ brandName }
        componentKey={ componentKey }
        displayBookmark={ displayBookmark }
        externalActionElement={ formattedActionFullyClickableable }
        externalBookmarkAction={ formattedFavorite }
        externalFooterActionElement={ formattedAction }
        externalMediaElement={ formattedMedia }
        fullyClickableParent={ true }
        kitPrice={ kitPrice }
        kitPriceAccessibilityLabel={ accessibilityLabelKitPrice }
        listPrice={ listPrice }
        listPriceAccessibilityLabel={ accessibilityLabelListPrice }
        originalPriceLabel={ accessibilityLabelOriginalPrice }
        priceLabel={ priceLabel }
        productName={ productName }
        promoText={ promoText }
        rating={ rating }
        reviewAccessibilityLabel={ accessibilityRating }
        reviewCount={ reviewCount }
        salePrice={ salePrice }
        salePriceAccessibilityLabel={ accessibilityLabelSalePrice }
        sponsoredBadgeLabel={ sponsoredBadgeLabel }
        tagsBottom={ productCardTags }
        tagsTop={ badgeTags }
        variantLabel={ variantLabel }
      />
    </div>
  );
};

/**
 * Method to handle on onIntersection event
 * @param {object} data
 * @param {function} onIntersectionEvent
 */
export const onIntersection = ( data, methods ) => {
  const { skuId } = data || {};
  const { onIntersectionEvent } = methods || {};
  if( skuId && onIntersectionEvent ){
    onIntersectionEvent( skuId );
  }
};

/**
 * Method to handle onDataCapture event
 * @param {object} data
 * @param {function} onDataCaptureEvent
 */
export const onDataCapture = ( data, methods ) => {
  const { skuId } = data || {};
  const { onDataCaptureEvent } = methods || {};
  if( skuId && onDataCaptureEvent ){
    onDataCaptureEvent( skuId );
  }
};

/**
 * Method to handle intersection events for data capture and other events.
 * @param {object} data - The data object.
 * @param {object} data.hasFiredDCEvent - Ref to track if the data capture event has fired.
 * @param {boolean} data.hasIntersected - Whether the component has intersected with the viewport.
 * @param {number} data.intersectionRatio - The intersection ratio from useIntersectionObserver.
 * @param {string} data.skuId - The SKU ID of the product.
 * @param {object} methods - The methods object.
 * @param {function} methods.onDataCaptureEvent - The event handler for data capture.
 * @param {function} methods.onIntersectionEvent - The event handler for intersection.
 */
export const handleIntersection = ( data, methods ) => {
  const { hasFiredDCEvent, hasIntersected, intersectionRatio, skuId } = data;
  const { onDataCaptureEvent, onIntersectionEvent } = methods;

  if( intersectionRatio === 1 ){
    utils.onIntersection( { skuId }, { onIntersectionEvent } );
  }
  else if( !hasFiredDCEvent.current && hasIntersected && intersectionRatio >= 0.5 ){
    hasFiredDCEvent.current = true;
    utils.onDataCapture( { skuId }, { onDataCaptureEvent } );
  }
};

/**
 * Method to handle action.after callback for atbAction
 * @param {object} methods methods
 * @param {action} methods.closeOverlay utility from useOverlay to close overlay
 * @param {action} methods.hideLoader utility from useLoader to hide loader
 * @param {action} inputData args
 */
export const composeActionAfter = ( methods ) => () => {
  const { hideLoader } = methods || {};

  if( !hideLoader ){
    return;
  }

  hideLoader();
};

/**
 * Method to handle invoking add to bag from product card actions
 * @param {object} methods methods
 * @param {action} methods.invokeMutation handler from layerhost to invoke DXL mutation
 * @param {string} action DXL action
 */
export const composeOnClick = ( data, methods ) => ( ) => {
  const { skuId, productId, isAddOverlay, atbAction } = data || {};
  const { invokeMutation, showLoader, handleActionAfter } = methods || {};
  const { graphql, customHeaders, params, variables } = atbAction || {};

  if( !invokeMutation || !skuId || !productId || !atbAction || !showLoader || isAddOverlay ){
    return;
  }

  showLoader();

  const mutation = {
    after: handleActionAfter,
    graphql,
    customHeaders,
    params,
    variables
  };

  invokeMutation( mutation );
};

/**
 * Creates a fully clickable link for the product card.
 * @param {object} data - The data for the link.
 * @param {object} data.action - The action object for the link.
 * @param {boolean} data.isFocusable - Determines if the link is focusable.
 * @param {string} data.screenReaderText - The text for screen readers.
 * @returns {React.Component} The Link_Huge component.
 */
export const createAbsoluteLink = ( data ) => {
  const {
    action,
    isFocusable,
    screenReaderText
  } = data;

  if( !action ){
    return;
  }

  return (
    <Link_Huge
      action={ action }
      fullyClickableParent={ true }
      screenReaderText={ screenReaderText }
      tabIndex={ isFocusable ? 0 : -1 }
    />
  );
};

/**
 * Creates the bookmark link/button for the product card.
 * @param {object} data - The data for the bookmark link.
 * @param {string} data.bookmarkLabel - The aria-label for the bookmark button.
 * @param {string} data.image - The product image URL.
 * @param {boolean} data.isButtonClicked - State to track if the button has been clicked.
 * @param {string} data.listPrice - The product's list price.
 * @param {array} data.messages - Messages to display in the remove favorite dialog.
 * @param {string} data.productName - The name of the product.
 * @param {object} data.removeFavoriteAction - The action to perform when removing a favorite.
 * @param {object} data.removeFavoriteDialog - The configuration for the remove favorite dialog.
 * @param {string} data.skuId - The SKU ID of the product.
 * @param {object} methods - The methods for the bookmark link.
 * @param {function} methods.invokeMutation - Function to invoke a GraphQL mutation.
 * @param {function} methods.setIsButtonClicked - Function to update the button clicked state.
 * @returns {React.Component|null} The bookmark Button component or null.
 */
export const createFavoritesButton = ( data, methods ) => {
  const {
    bookmarkLabel,
    image,
    isButtonClicked,
    listPrice,
    messages,
    productName,
    removeFavoriteAction,
    removeFavoriteDialog,
    salePrice,
    skuId
  } = data;

  const { invokeMutation, setIsButtonClicked } = methods;

  if( !removeFavoriteDialog ){
    return null;
  }

  return (
    <Button
      action={ {
        ...removeFavoriteAction,
        navigationType: 'modal'
      } }
      ariaLabel={ bookmarkLabel }
      ariaHiddenIcon={ true }
      iconOnly
      iconImage={ removeFavoriteAction ? 'HeartFilled' : 'HeartEmpty' }
      iconSize={ 'lg' }
      onClick={ ( e ) => {
        setIsButtonClicked( true );
        e.stopPropagation();
        e.preventDefault();
      } }
      overlayContent={
        <RemoveItem
          headerText={ removeFavoriteDialog.title }
          confirmLabel={ removeFavoriteDialog.confirmLabel }
          confirmMessage={ removeFavoriteDialog.message }
          cancelLabel={ removeFavoriteDialog.cancelLabel }
          action={ removeFavoriteAction }
          invokeMutation={ invokeMutation }
          moduleParams={ { 'skuId': skuId } }
          messages={ removeFavoriteMessage( isButtonClicked, messages ) }
          renderComponent={ {
            image: {
              'imageUrl': image,
              'width': 70,
              'metaData': { width: 70 }
            },
            productName: productName,
            salePrice: listPrice || salePrice,
            moduleName: 'MiniProductCard'
          } }
        /> }
      tertiary
      tiny
    ></Button>
  );
};

/**
 * Creates the action button (Add to Bag or View Options) for the product card footer.
 * @param {object} data - The data for the button.
 * @param {object} data.addToBagAction - The action for adding to bag.
 * @param {object} data.atbAction - The composed add to bag action.
 * @param {string} data.brandName - The brand name of the product.
 * @param {boolean} data.isFocusable - Determines if the button is focusable.
 * @param {string} data.productName - The name of the product.
 * @param {object} data.viewOptionAction - The action for viewing options.
 * @param {object} methods - The methods for the button.
 * @param {function} methods.handleOnClick - The click handler for the add to bag button.
 * @returns {React.Component|null} The Button component or null.
 */
export const createButton = ( data, methods ) => {
  const {
    addToBagAction,
    atbAction,
    brandName,
    isFocusable,
    productName,
    viewOptionAction
  } = data;

  const { handleOnClick } = methods;

  if( !addToBagAction && !viewOptionAction ){
    return null;
  }

  return (
    <>
      { !viewOptionAction && addToBagAction && (
        <Button
          tabIndex={ isFocusable ? 0 : -1 }
          ariaHiddenIcon={ true }
          compact={ true }
          onClick={ handleOnClick }
          action={ atbAction }
          label={ atbAction.label }
        ></Button>
      ) }
      { viewOptionAction && (
        <Button
          tabIndex={ isFocusable ? 0 : -1 }
          ariaLabel={ `${brandName} ${productName} ${viewOptionAction.label}` }
          ariaHiddenIcon={ true }
          compact={ true }
          secondary={ true }
          onClick={ ( e ) => {
            e.stopPropagation();
            e.preventDefault();
            handleRedirect( { url: viewOptionAction.url } );
          } }
          action={ viewOptionAction }
          label={ viewOptionAction.label }
        ></Button>
      ) }
    </>
  );
};

/**
 * Creates the image component for the product card.
 * @param {object} data - The data for the image.
 * @param {string} data.image - The source URL for the image.
 * @param {number} data.imgDimension - The calculated dimension for the image.
 * @param {boolean} data.prefetchImage - Whether to prefetch the image.
 * @param {string} data.screenReaderText - The alt text for the image.
 * @returns {React.Component} The Image component.
 */
export const createImage = ( data ) => {
  const {
    image,
    imgDimension,
    prefetchImage,
    screenReaderText
  } = data;

  if( !image ){
    return;
  }

  return (
    <Image
      altText={ screenReaderText }
      ariaHidden={ true }
      aspectRatio={ '1:1' }
      metaData={ { width: imgDimension, height: imgDimension } }
      prefetch={ prefetchImage }
      shouldUseIntersectionObserver={ false }
      src={ image }
    />
  );
};

/**
 * Set the remove favorite message
 * @param {boolean} isButtonClicked
 * @param {array} messages
 */
export const removeFavoriteMessage = ( isButtonClicked, messages ) => {
  return ( isButtonClicked && hasItems( messages ) ) ? [] : messages;
};

/**
 * Function to return image params as per type and size.
 *
 * @method setImageParams
 * @param {string} type - This is the type of Product Card, i.e full/regular/compact.
 * @param {string} size - This is the size of the Product Card, i.e small/xLarge.
 * @returns {string} Returns string consisting width and height as per type and size
 **/

export const setImageParams = ( type, size ) => {
  const cardTypeSize = `${type}_${size}`.toLocaleLowerCase();

  switch ( cardTypeSize ){
    case 'compact_small':
      return 120;

    case 'full_small':
    case 'regular_xlarge':
    case 'compact_xlarge':
      return 240;

    case 'full_xlarge':
      return 360;

    case 'regular_small':
      return 160;

    default:
      return 240; // since type full and nSize small are the default props values
  }
};

/**
 * Property type definitions
 * @typedef ProductCardProps
 * @type {object}
 * @property {object} action - This the the action object which have navigational url on click of PC
 * @property {string} additionalOffersText - This is the additionalOffersText for the product
 * @property {object} addToBagAction - Sets label and graphql for addtobag button
 * @property {string} altImage - This is the alternate Image of product
 * @property {string} ariaLabelBookmark - This is the aria label for bookmark
 * @property {string} badge - This is the badge i.e new Arrival, coming soon etc
 * @property {array} badgeTags - This is the badge i.e new Arrival, coming soon etc
 * @property {string} bookmarkAccessibility - This is the bookmarkAccessibility for the product
 * @property {boolean} bookmarked - This will set bookmarked for the product
 * @property {string} brandName - This is name of brand
 * @property {number} componentKey - Sets the component key
 * @property {number} contentId - Sets the content id
 * @property {boolean} displayBookmark - This sets the flag to display bookmark
 * @property {string} image - This is the product Image
 * @property {function} invokeMutation - Sets the invoke Mutation to send child component
 * @property {boolean} isVisibleInCarousel - This is a flag to check if the ada tabindex is visible in carousel
 * @property {string} kitPrice - This is kitPrice of product
 * @property {string} listPrice - This is listPrice of product
 * @property {string} listPriceAccessibilityLabel - Sets the list price accessibility label
 * @property {string} maxQuantity - This sets the max quantity of product to be added to bag
 * @property {array} messages - Sets the error message
 * @property {function} onDataCaptureEvent - This is the onDataCaptureEvent for the product
 * @property {function} onIntersectionEvent - This is the onIntersectionEvent for the product
 * @property {boolean} prefetchImage - This is a flag to check the image should be pre fetched
 * @property {string} priceLabel - This is the priceLabel of product
 * @property {array} productCardTags - This sets the product card tags for the product
 * @property {string} productId - This is the product ID
 * @property {string} productName - This is name of product
 * @property {string} promoText - This is the promo text for the product
 * @property {number} rating - This is the rating of the product
 * @property {object} removeFavoriteAction - Sets the remove Favorite Action
 * @property {object} removeFavoriteDialog - Sets the remove Favorite Dialog
 * @property {string} reviewAccessibilityLabel - This is the reviewAccessibilityLabel for the product
 * @property {number} reviewCount - This is the reviewCount for the product
 * @property {string} salePrice - This is the salePrice of product
 * @property {string} salePriceAccessibilityLabel - Sets the sale price accessibility label
 * @property {string} size - This is the type of size of PC (currently one of 'small', 'xLarge')
 * @property {string} skuId - This is the sku
 * @property {string} sponsoredBadgeLabel - This is the sponsored badge label
 * @property {string} type - This is the type of Product card going forward PC (currently one of 'full', 'regular', 'compact')
 * @property {string} variantLabel - This is variant label of Product
 * @property {object} viewOptionAction - Sets label and graphql for options button
 */
export const propTypes = {
  /** This is the navigational url on click of PC */
  action: PropTypes.shape( {
    url: PropTypes.string
  } ),
  /** This is the additionalOffersText for the product */
  additionalOffersText: PropTypes.string,
  /** This sets the label and graphql for add to button */
  addToBagAction: PropTypes.object,
  /** This is the alternate Image of product */
  altImage: PropTypes.string,
  /** This is the aria label for bookmark */
  ariaLabelBookmark: PropTypes.string,
  /** This is the badge i.e new Arrival, coming soon etc */
  badge: PropTypes.string,
  /** Sets the badgeTags */
  badgeTags: PropTypes.array,
  /** This is the bookmarkAccessibility for the product */
  bookmarkAccessibility: PropTypes.string,
  /** This will set bookmarked for the product */
  bookmarked: PropTypes.bool,
  /** This is name of brand */
  brandName: PropTypes.string,
  /** Sets the carousel index from swiper */
  carouselIndex: PropTypes.number,
  /** Sets the component key */
  componentKey: PropTypes.number,
  /** Sets the content id */
  contentId: PropTypes.oneOfType( [PropTypes.number, PropTypes.string] ),
  /** This sets additional dataCaptureAttributes this is not used in display */
  dataCapureAttributes: PropTypes.object,
  /** This sets the flag to display bookmark */
  displayBookmark: PropTypes.bool,
  /** This is the product Image */
  image: PropTypes.string,
  /** Sets the remove invoke Mutation */
  invokeMutation: PropTypes.func,
  /** This is kitPrice of product */
  kitPrice: PropTypes.string,
  /** This is listPrice of product */
  listPrice: PropTypes.string,
  /** Sets the list price accessibility label */
  listPriceAccessibilityLabel: PropTypes.string,
  /** This sets the max quantity of product to be added to bag */
  maxQuantity: PropTypes.number,
  /** Sets the messages */
  messages: PropTypes.array,
  /**  This is the onDataCaptureEvent for the product */
  onDataCaptureEvent: PropTypes.func,
  /**  This is the onIntersectionEvent for the product */
  onIntersectionEvent: PropTypes.func,
  /** Sets the prefetch status to the image */
  prefetchImage: PropTypes.bool,
  /** This is price label of product */
  priceLabel: PropTypes.string,
  /** This sets the product card tags for the product */
  productCardTags: PropTypes.array,
  /** This is product ID of product */
  productId: PropTypes.string,
  /** This is name of product */
  productName: PropTypes.string,
  /** This is the promo text for the product */
  promoText: PropTypes.string,
  /** This is the rating of the product */
  rating: PropTypes.number,
  /** Sets the remove Favorite Action */
  removeFavoriteAction: PropTypes.object,
  /** Sets the remove Favorite Dialog */
  removeFavoriteDialog: PropTypes.object,
  /**  This is the reviewAccessibilityLabel for the product */
  reviewAccessibilityLabel: PropTypes.string,
  /** This is the reviewCount for the product */
  reviewCount: PropTypes.number,
  /** This is salePrice of product */
  salePrice: PropTypes.string,
  /** Sets the sale price accessibility label */
  salePriceAccessibilityLabel: PropTypes.string,
  /** This is the type of size of PC (currently one of 'small', 'xLarge') */
  size: PropTypes.oneOf( [
    'small',
    'xLarge'
  ] ),
  /** This is sku of product */
  skuId: PropTypes.string,
  /** This is the sponsored badge label */
  sponsoredBadgeLabel: PropTypes.string,
  /** This is the type of Product card going forward PC (currently one of 'full', 'regular', 'compact') */
  type: PropTypes.oneOf( [
    'full',
    'regular',
    'compact'
  ] ),
  /** This is  variant label for prduct i.e colors/sizes */
  variantLabel: PropTypes.string,
  /** This sets the label and graphql for options button */
  viewOptionAction: PropTypes.object
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {boolean} displayBookmark=false - Flag to specify if the display bookmark is available, default is false
 * @property {boolean} prefetchImage = true - Flag to specify if image should be prefetched
 * @property {string} size='small' - The product card size, default is small.
 * @property {string} type='full' - The product card type, default is full.
 */
export const defaultProps = {
  displayBookmark: false,
  prefetchImage: true,
  size: 'small',
  type: 'full',
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};

ProductCard.propTypes = propTypes;
ProductCard.defaultProps = defaultProps;

export default ProductCard;